import {
  OnInit,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  NgZone,
  ChangeDetectorRef,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ClaimsService } from "src/app/_services/common/claims.service";
import { Claims } from "src/app/_models/common/claims";
import { IApiresponse, ApiRequest, IApiRequest } from "src/app/_models";
import { AppGridComponent } from "src/app/shared/grid/grid-component/grid.component";
import { ColDef } from "ag-grid-community";
import { GridConfig } from "src/app/_models/common/grid/grid-config";
import { CloseFormAction } from "src/app/_models/advance-mileage/org-ukmileage-rate-info";
import { UkMileageRateSettingService } from "src/app/_services/advance-mileage/uk-mileage-rate-setting.service";
import { TranslateService } from "@ngx-translate/core";
import { OfficePostcodeStatus, ActivationRequestStatus, ActivationRequestType } from "src/app/_models/enum/solo-enum";
import { ActivationRequestModel } from "src/app/_models/advance-mileage/org-office-codes";

@Component({
    selector: "office-address-panel",
    templateUrl: "./office-address-panel.component.html",
    standalone: false
})
export class OfficeAddressPanelComponent implements OnInit {
  @Output() closePanelEvent = new EventEmitter<any>();
  claims: Claims;
  isOverlayEnabled: boolean;
  param: string;
  userActionEventSubscription: Subscription;
  @ViewChild(AppGridComponent) gridComponent: AppGridComponent;
  gridColumnDefs: ColDef[];
  gridConfigItems: GridConfig;
  gridData: any = [];
  gridFilterStatus: number;
  //Ag Grid Variable Declaration
  isOverlayRequired = true;
  hasExpiredLocations: boolean = false;
  closeFormAction: CloseFormAction = new CloseFormAction();
  constructor(
    public zone: NgZone,
    private ukMileageRateSettingService: UkMileageRateSettingService,
    private translate: TranslateService,
    private claimsService: ClaimsService,
    private cdfr: ChangeDetectorRef
  ) {
    this.isOverlayEnabled = false;
    this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
    });
  }

  ngOnInit() {
    this.fnInitialiseGrid();
    this.fnGetGridItems();
  }

  //Column Definitions
  fnCreateColumnDefs() {
    this.gridColumnDefs = [
      {
        colId: "officeName",
        headerName: this.translate.instant("uk_mileage_office_name"),
        field: "officeName",
        resizable: true,
        sortable: true,
        suppressHeaderMenuButton: true,
        minWidth: 100,
        width: 120,
        cellClass: "cursor-pointer",
      },
      {
        colId: "officeAddress",
        headerName: this.translate.instant("uk_mileage_office_address"),
        field: "officeAddress",
        resizable: true,
        sortable: true,
        suppressHeaderMenuButton: true,
        minWidth: 100,
        cellClass: "cursor-pointer",
      },
      {
        headerName: this.translate.instant("label_status"),
        colId: "status",
        field: "status",
        resizable: true,
        sortable: true,
        suppressHeaderMenuButton: true,
        cellRenderer: "fieldValueRenderer",
        minWidth: 100,
        headerComponentParams: [
          {
            class: "active-color font-weight-bold",
            value: OfficePostcodeStatus.Active,
            text: this.translate.instant("label_active"),
          },
          {
            class: "not-invited-color font-weight-bold",
            value: OfficePostcodeStatus.Expired,
            text: this.translate.instant("label_expired"),
          },
        ],
      },
    ];

    if (this.hasExpiredLocations) {
      this.gridColumnDefs.push({
        headerName: "",
        colId: "userActionField",
        cellRenderer: 'buttonRenderer',
        field: 'userActionField',
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        width: 300,
        minWidth: 300,
        cellClass: 'text-center d-flex align-items-center',
        headerComponentParams: [
          {
            buttonClass: 'btn-primary',
            buttonText: this.translate.instant('request_to_activate'),
            disableProperty: '',
            hideProperty: 'hideRequestActivate',
            userAction: 'requestActivate',
            textBeforeBtn: ''
          },
          {
            buttonClass: 'btn-secondary',
            buttonText: this.translate.instant('request_sent'),
            disableProperty: 'disableRequestSent',
            hideProperty: 'hideRequestSent',
            userAction: 'requestSent',
            textBeforeBtn: ''
          },
        ]
      });
    }
  }

  //Grid Config Items
  fnGetGridConfigItems() {
    this.gridConfigItems = new GridConfig(
      true,
      true,
      "officeName",
      "officePostcodeId",
      0,
      false,
      [],
      true,
      true,
      false,
      null,
      "asc",
      40,
      "no_data_availabe",
      null,
      null,
      false,
      false,
      "",
      "",
      false
    );
  }

  //Initialise Grid
  fnInitialiseGrid() {
    this.fnCreateColumnDefs();
    this.fnGetGridConfigItems();
    setTimeout(() => {
      this.gridComponent.initializeGrid();
    }, 100);
  }

  fnOnGridCellClick(params) {
    if (params.data.userAction == "requestActivate") {
      this.fnRequestActivateOffice(params.data);
    } else if(params.colDef.colId != 'userActionField' && params.data.status != OfficePostcodeStatus.Expired){
      this.closePanelEvent.emit(params.data);
    }
    params.data.userAction = null;
  }

  fnRequestActivateOffice(item: any) {
    let request = new ApiRequest<ActivationRequestModel>(
      "ActivationRequest",
      1.0,
      ""
    );
    let req = new ActivationRequestModel();
    req.id = item.officePostcodeId;
    req.type = ActivationRequestType.OfficePostcode;
    request.requestInfo = req;
    this.ukMileageRateSettingService
      .SoloSaveActivationRequest(request)
      .subscribe((res: IApiresponse<string>) => {
        this.fnGetGridItems();
      });
  }

  fnGetGridItems() {
    this.ukMileageRateSettingService
      .SoloGetOrgOfficeCodes()
      .subscribe((res: IApiresponse<any>) => {
        this.gridData = res.responseInfo.officeDetailsList;
        this.hasExpiredLocations = this.gridData.some(
          (d) => d.status === OfficePostcodeStatus.Expired
        );
        this.gridData.forEach((d) => {
          d.hideRequestActivate = d.status !== OfficePostcodeStatus.Expired || d.activationRequestStatus === ActivationRequestStatus.RequestSent;
          d.hideRequestSent = d.status !== OfficePostcodeStatus.Expired || d.activationRequestStatus !== ActivationRequestStatus.RequestSent;
          d.disableRequestSent = true;
        });
        this.fnCreateColumnDefs();
        this.fnGetGridConfigItems();
        setTimeout(() => {
          this.gridComponent.refreshGridData();
        }, 300);
        this.cdfr.detectChanges();
      });
  }

  onClosePanel() {
    this.closePanelEvent.emit(false);
  }
}
